import { useParams, Link } from 'react-router-dom';
import { games } from '@/mocks/games';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/site';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const game = games.find((g) => g.slug === slug);
  const article = articles.find((a) => a.slug === slug);
  const content = game || article;

  if (!content) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="font-heading text-xl text-foreground-800 mb-2">内容不存在</h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== slug)
    .slice(0, 3);

  const relatedGames = games
    .filter((g) => g.slug !== slug)
    .slice(0, 3);

  const isArticle = 'content' in content && typeof content.content === 'string';
  const title = 'title' in content ? content.title : '';
  const image = 'image' in content ? content.image : '';
  const description = 'excerpt' in content ? content.excerpt : ('description' in content ? content.description : '');
  const author = 'author' in content ? content.author : '';
  const date = 'date' in content ? content.date : '';

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    image: image,
    author: {
      '@type': 'Person',
      name: author,
    },
    datePublished: date,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <div className="min-h-screen pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: title },
            ]}
          />

          <article>
            <header className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-900 mb-4 leading-tight">
                {title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                {author && (
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-line"></i>
                    </div>
                    {author}
                  </span>
                )}
                {date && (
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line"></i>
                    </div>
                    {date}
                  </span>
                )}
                {'readTime' in content && (
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line"></i>
                    </div>
                    {content.readTime}阅读
                  </span>
                )}
              </div>
            </header>

            {image && (
              <div className="relative aspect-[16/9] rounded-xl overflow-hidden mb-8">
                <img
                  src={image}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {isArticle && (
              <div
                className="prose-custom text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: (content as typeof articles[0]).content }}
              />
            )}

            {!isArticle && 'description' in content && (
              <div className="bg-background-100 rounded-xl p-6 md:p-8 mb-8">
                <p className="text-foreground-700 leading-relaxed text-base">
                  {content.description}
                </p>
                {'difficulty' in content && (
                  <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-background-50 rounded-lg p-4 text-center">
                      <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                        <i className="ri-bar-chart-line"></i>
                      </div>
                      <p className="text-xs text-foreground-500">难度</p>
                      <p className="font-heading text-sm text-foreground-800">{content.difficulty}</p>
                    </div>
                    <div className="bg-background-50 rounded-lg p-4 text-center">
                      <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
                        <i className="ri-group-line"></i>
                      </div>
                      <p className="text-xs text-foreground-500">人数</p>
                      <p className="font-heading text-sm text-foreground-800">{content.players}</p>
                    </div>
                    <div className="bg-background-50 rounded-lg p-4 text-center">
                      <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center rounded-full bg-secondary-50 text-secondary-500">
                        <i className="ri-timer-line"></i>
                      </div>
                      <p className="text-xs text-foreground-500">时长</p>
                      <p className="font-heading text-sm text-foreground-800">{content.time}</p>
                    </div>
                    <div className="bg-background-50 rounded-lg p-4 text-center">
                      <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center rounded-full bg-amber-50 text-amber-500">
                        <i className="ri-star-fill"></i>
                      </div>
                      <p className="text-xs text-foreground-500">评分</p>
                      <p className="font-heading text-sm text-foreground-800">{content.rating}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </article>

          {relatedArticles.length > 0 && (
            <section className="mt-12 mb-12 border-t border-background-200 pt-10">
              <h3 className="font-heading text-xl text-foreground-900 mb-6">相关攻略</h3>
              <div className="flex flex-col gap-4">
                {relatedArticles.map((a) => (
                  <Link
                    key={a.id}
                    to={`/article/${a.slug}`}
                    className="group flex flex-col sm:flex-row gap-4 p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-full sm:w-40 h-28 rounded-lg overflow-hidden shrink-0">
                      <img src={a.image} alt={a.title} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <h4 className="font-heading text-base text-foreground-900 group-hover:text-primary-500 transition-colors mb-1">
                        {a.title}
                      </h4>
                      <p className="text-sm text-foreground-500">{a.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {relatedGames.length > 0 && (
            <section className="mb-12">
              <h3 className="font-heading text-xl text-foreground-900 mb-6">相关游戏</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedGames.map((g) => (
                  <Link
                    key={g.id}
                    to={`/article/${g.slug}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={g.image} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-3">
                      <h4 className="font-heading text-sm text-foreground-800 group-hover:text-primary-500 transition-colors">
                        {g.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}